import React, { useState } from "react";
import "./AddtionalContactsTable.css";
import "./RegistrationView.css";
import { PhoneNumberInput } from "./PhoneNumberInput";
import { RegistrationNotificationContact, RegistrationPhoneNumber } from "../../entities/RegistrationEntities";

import MultiSelectCheckbox, { Option } from "../../components/multiSelectCheckbox/MultiSelectCheckbox";

interface AddtionalContactsTableProps {
    onAdd: (contact: RegistrationNotificationContact) => void;
    onDelete: (index: number) => void;
    addtionalContacts: RegistrationNotificationContact[];
}

const emailNotificationPreferences: Option[] = [
    { label: "Sales Quote", value: "Sales Quote" },
    { label: "Sales Order Confirmation", value: "Sales Order Confirmation" },
    { label: "Sales Order Completion", value: "Sales Order Completion" },
    { label: "Sales Order Processing", value: "Sales Order Processing" },
    { label: "Sales Invoice", value: "Sales Invoice" },
    { label: "Sales Cr Memo", value: "Sales Cr Memo" },
    { label: "Feedback", value: "Feedback" },
];

const AddtionalContactsTable: React.FC<AddtionalContactsTableProps> = ({ onAdd, addtionalContacts, onDelete }) => {
    const [showModal, setShowModal] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [emailAddress, setEmailAddress] = useState("");
    const [phoneNumber, setPhoneNumber] = useState<RegistrationPhoneNumber>(new RegistrationPhoneNumber());
    const [wantsTextNotifications, setWantsTextNotifications] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleModal = () => {
        setShowModal((prev) => !prev);
        setFirstName("");
        setLastName("");
        setEmailAddress("");
        setSelectedOptions([]);
        setPhoneNumber(new RegistrationPhoneNumber());
        setWantsTextNotifications(false);
        setError(null);
    };

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddContact = () => {
        if (!firstName.trim() || !lastName.trim()) {
            setError("First name and last name are required.");
            return;
        }

        if (firstName.length > 50 || lastName.length > 50) {
            setError("First name and last name must be less than 50 characters.");
            return;
        }

        if (!emailAddress.trim() || !isValidEmail(emailAddress)) {
            setError("Please enter a valid email address.");
            return;
        }

        if (selectedOptions.length === 0) {
            setError("At least one notification option must be selected.");
            return;
        }

        if (!isValidPhoneNumber(phoneNumber.number || "")) {
            setError("Please enter a valid phone number.");
            return;
        }

        const formattedPhone = formatPhoneNumber(phoneNumber);

        const newContact: RegistrationNotificationContact = {
            firstName,
            lastName,
            emailAddress,
            phoneNumber: formattedPhone,
            emailNotificationPreferences: selectedOptions,
            receiveTextNotifications: wantsTextNotifications
        };

        onAdd(newContact);
        toggleModal();
    };

    const formatPhoneNumber = (phoneNumber: RegistrationPhoneNumber) => {
        const { countryCode, number } = phoneNumber;
        if (number === null || number === "") {
            return "";
        }

        const formattedNumber = number.replace(/^0+/, "");

        if (formattedNumber === "") {
            return "";
        }

        const formattedPhoneNumber = countryCode + formattedNumber;
        return formattedPhoneNumber;
    }

    const isValidPhoneNumber = (phoneNumber: string) => {
        const regex = /^(\d)(?!\1{5,})\d{6,}$/;
        const isValid = regex.test(phoneNumber);

        return isValid;
    }

    const onChangePhoneNumber = (name: string, value: RegistrationPhoneNumber) => {
        setPhoneNumber(value);
    }

    return (
        <div>
            <div className="table-container">
                <table className="contacts-table">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Phone Number</th>
                            <th>Email Address</th>
                            <th>
                                Tick the required email notifications this contact is to receive
                            </th>
                            <th>Receive Text Message Notifications</th>

                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {addtionalContacts.length === 0 ? <tr className="no-contacts-row">
                            <td colSpan={7}>
                                There are no <strong>contacts</strong>.
                            </td>
                        </tr> :
                            <>
                                {addtionalContacts.map((c, index) => <tr key={index}>
                                    <td>
                                        {c.firstName}
                                    </td>
                                    <td>
                                        {c.lastName}
                                    </td>
                                    <td>
                                        {c.phoneNumber}
                                    </td>
                                    <td>
                                        {c.emailAddress}
                                    </td>
                                    <td>
                                        {c.emailNotificationPreferences.join(", ")}
                                    </td>
                                    <td>
                                        {c.receiveTextNotifications ? "Yes" : "No"}
                                    </td>
                                    <td className="delete" onClick={() => { onDelete(index) }}>Delete</td>
                                </tr>)}
                            </>}

                    </tbody>
                </table>
            </div>

            <div className="button-container">
                <button onClick={toggleModal} className="add-contact-btn">
                    Add Contact
                </button>
            </div>

            {showModal && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <div className="modal-header">Add Contact</div>
                        <div className="modal-body">
                            
                            <div className="reg-view-row">
                                <div className="reg-view-control required">
                                    <span>First Name</span>
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        name="addtional-contact-firstname"
                                        maxLength={50}
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                </div>
                                <div className="reg-view-control required">
                                    <span>Last Name</span>
                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        name="addtional-contact-lastname"
                                        maxLength={50}
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="reg-view-row">
                                <div className="reg-view-control required">
                                    <span>Email Address</span>
                                    <input
                                        type="text"
                                        placeholder="Email Address"
                                        name="addtional-contact-emailAddress"
                                        value={emailAddress}
                                        onChange={(e) => setEmailAddress(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="reg-view-row">
                                <div className="reg-view-control required">
                                    <span>Phone Number</span>
                                    <PhoneNumberInput
                                        name={'addtional-contact-mobilenumber'}
                                        value={phoneNumber}
                                        onChange={onChangePhoneNumber}
                                    />
                                </div>
                            </div>

                            <div className="reg-view-row">
                                <div className="reg-view-control required">
                                    <span>
                                        Tick the required email notifications this contact is to receive for galvanising, grating, and sandblasting orders
                                    </span>
                                    <MultiSelectCheckbox
                                        options={emailNotificationPreferences}
                                        value={selectedOptions}
                                        onChange={setSelectedOptions}
                                    />
                                </div>
                            </div>

                            <div className="reg-view-checkbox">
                                <input
                                    type="checkbox"
                                    id="addtional-contact-receive-text-msg"
                                    name="addtional-contact-receive-text-msg"
                                    checked={wantsTextNotifications}
                                    onChange={(e) => setWantsTextNotifications(e.target.checked)}
                                />
                                <label>
                                    Tick if you would this contact to receive text message notifications as well
                                </label>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                        </div>

                        <div className="modal-footer">
                            <button className="cancel" onClick={toggleModal}>Cancel</button>
                            <button className="add" type="button" onClick={handleAddContact}>Add Contact</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddtionalContactsTable;